(() => {
  'use strict';
  const ELEMENT = 'pfg-sw1-combo-slideshow';
  // The snippet can occur in multiple sections; duplicate asset tags are safe.
  if (customElements.get(ELEMENT)) return;

  class PfgSw1ComboSlideshow extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.userPaused = false;
      this.inViewport = false;
      this.hovered = false;
      this.focused = false;
      this.timer = null;
      this.requestId = 0;
      this.pendingIndex = null;
    }

    connectedCallback() {
      this.images = [...this.querySelectorAll('.pfg-sw1-combo__image')].slice(0, 5);
      this.controls = this.querySelector('.pfg-sw1-combo__controls');
      this.counter = this.querySelector('[data-pfg-combo-count]');
      this.status = this.querySelector('[data-pfg-combo-status]');
      this.toggle = this.querySelector('[data-pfg-combo-action="toggle"]');
      this.toggleIcon = this.querySelector('[data-pfg-combo-toggle-icon]');
      if (this.images.length < 2 || !this.controls || !this.toggle || !this.counter || !this.status || !this.toggleIcon) return;

      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motionListener = () => this.sync();
      this.motion.addEventListener('change', this.motionListener);
      this.controls.hidden = false;
      this.counter.hidden = false;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-pfg-combo-action]');
        if (!button || !this.controls.contains(button)) return;
        // Controls never follow Dawn's stretched product-card link or quick-add.
        event.preventDefault();
        event.stopPropagation();
        const action = button.dataset.pfgComboAction;
        if (action === 'toggle') {
          if (this.motion.matches) return;
          this.userPaused = !this.userPaused;
          this.status.textContent = this.userPaused ? 'Slideshow paused.' : 'Automatic slideshow enabled. It pauses while controls have focus.';
          this.sync();
        } else {
          this.userPaused = true;
          this.show((this.pendingIndex ?? this.index) + (action === 'next' ? 1 : -1), true);
        }
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        this.userPaused = true;
        this.show((this.pendingIndex ?? this.index) + (event.key === 'ArrowRight' ? 1 : -1), true);
      }, { signal });
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.hovered = true;
        this.sync();
      }, { signal });
      this.addEventListener('pointerleave', () => { this.hovered = false; this.sync(); }, { signal });
      this.addEventListener('focusin', () => { this.focused = true; this.sync(); }, { signal });
      this.addEventListener('focusout', (event) => {
        this.focused = this.contains(event.relatedTarget);
        this.sync();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.inViewport = entry.isIntersecting && entry.intersectionRatio >= 0.2;
          this.sync();
        }, { threshold: [0, 0.2] });
        this.observer.observe(this);
      } else {
        // Manual controls remain usable in older browsers, without background timers.
        this.inViewport = false;
      }
      this.render(this.index);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.timer = null;
      this.abort?.abort();
      this.observer?.disconnect();
      this.motion?.removeEventListener('change', this.motionListener);
      this.inViewport = false;
      this.hovered = false;
      this.focused = false;
      this.requestId += 1;
      this.pendingIndex = null;
    }

    prepare(image) {
      if (image.pfgReady) return image.pfgReady;
      image.pfgReady = new Promise((resolve) => {
        let settled = false;
        let timeout;
        const done = (ready) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          image.removeEventListener('load', loaded);
          image.removeEventListener('error', failed);
          resolve(ready);
        };
        const failed = () => done(false);
        const loaded = () => {
          if (!image.naturalWidth) return failed();
          if (typeof image.decode === 'function') image.decode().then(() => done(true), failed);
          else done(true);
        };
        image.addEventListener('load', loaded);
        image.addEventListener('error', failed);
        timeout = setTimeout(failed, 15000);
        image.loading = 'eager';
        if (image.dataset.src) {
          image.srcset = image.dataset.srcset || '';
          image.src = image.dataset.src;
          delete image.dataset.src;
          delete image.dataset.srcset;
        }
        if (image.complete) loaded();
      });
      return image.pfgReady;
    }

    async show(next, announce = false) {
      const requested = (next + this.images.length) % this.images.length;
      const request = ++this.requestId;
      this.pendingIndex = requested;
      clearTimeout(this.timer);
      this.timer = null;
      const ready = await this.prepare(this.images[requested]);
      if (request !== this.requestId || !this.isConnected) return;
      this.pendingIndex = null;
      if (!ready) {
        this.userPaused = true;
        this.status.textContent = 'That combination image could not load. The current image is unchanged; please try another combination.';
        this.sync();
        return;
      }
      this.render(requested, announce);
    }

    render(index, announce = false) {
      this.index = index;
      const active = this.images[index];
      this.images.forEach((image, index) => { image.hidden = index !== this.index; });
      this.counter.textContent = `${this.index + 1} / ${this.images.length}`;
      if (announce) this.status.textContent = `Combination ${this.index + 1} of ${this.images.length}: ${active.alt}. Slideshow paused.`;
      this.sync();
    }

    sync() {
      clearTimeout(this.timer);
      this.timer = null;
      const reduced = this.motion.matches;
      const paused = this.userPaused || reduced;
      this.toggle.disabled = reduced;
      const label = reduced ? 'Automatic slideshow off: reduced motion enabled' : paused ? 'Play color combination slideshow' : 'Pause color combination slideshow';
      this.toggle.setAttribute('aria-label', label);
      this.toggle.title = label;
      this.toggleIcon.textContent = paused ? '\u25b6' : '\u275a\u275a';
      if (this.isConnected && this.pendingIndex === null && !paused && this.inViewport && !this.hovered && !this.focused && !document.hidden) {
        // Fetch/decode one next frame in advance; never blank the current hero.
        this.prepare(this.images[(this.index + 1) % this.images.length]);
        this.timer = setTimeout(() => this.show(this.index + 1), 5000);
      }
    }
  }
  customElements.define(ELEMENT, PfgSw1ComboSlideshow);
})();
